import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9opt2iftmtnfcfh0bsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 爆料网',
          description: '了解爆料网的品牌故事、发展历程、团队介绍与联系方式。爆料网致力于为用户提供最新、最全的爆料资讯服务。',
          url: 'https://www.baoliao.com/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Page Header */}
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于爆料网
              </h1>
              <p className="text-foreground-600 max-w-xl mx-auto leading-relaxed">
                爆料网是一个致力于汇聚全网最新爆料资讯的综合平台，为您提供最快、最全、最可靠的爆料内容服务。
              </p>
            </div>

            {/* Brand Story */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-book-open-line text-primary-500"></i>
                品牌故事
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-4">
                <p className="text-foreground-700 leading-relaxed text-sm">
                  爆料网成立于2023年，由一群资深的媒体人和技术专家共同创立。我们的初衷是打造一个汇聚全网爆料资讯的一站式平台，解决用户在不同平台之间来回切换寻找爆料内容的痛点。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  经过两年多的发展，爆料网已经成为国内领先的爆料资讯聚合平台，日均访问用户超过百万。我们与51爆料网、91爆料网等多家知名爆料平台建立了内容合作关系，确保用户能够第一时间获取最新、最热的爆料内容。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  我们始终坚持"真实、快速、全面"的内容理念，投入大量资源用于信息审核和内容筛选，力求为每一位用户提供高质量的爆料资讯阅读体验。
                </p>
              </div>
            </section>

            {/* Milestones */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-flag-line text-accent-500"></i>
                发展历程
              </h2>
              <div className="space-y-4">
                {[
                  { year: '2023年6月', title: '爆料网正式上线', desc: '核心团队组建完成，爆料网1.0版本正式发布，首月用户突破10万。' },
                  { year: '2023年12月', title: '内容合作拓展', desc: '与51爆料网、91爆料网达成内容合作，平台资讯量增长300%。' },
                  { year: '2024年3月', title: '日活突破50万', desc: '通过持续内容优化和用户体验提升，日均活跃用户突破50万大关。' },
                  { year: '2024年9月', title: '移动端适配升级', desc: '完成全站移动端适配，移动端访问占比达到70%。' },
                  { year: '2025年1月', title: '品牌升级', desc: '品牌视觉全面升级，新增分类导航和个性化推荐功能。' },
                  { year: '2025年6月', title: '用户突破百万', desc: '日均访问用户突破百万，成为行业领先的爆料资讯聚合平台。' },
                ].map((milestone, index) => (
                  <div
                    key={index}
                    className="flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="flex-shrink-0 w-16 pt-0.5">
                      <span className="text-xs font-bold text-accent-600 bg-accent-100 px-2 py-1 rounded-full whitespace-nowrap">
                        {milestone.year}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                        {milestone.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        {milestone.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-star-line text-accent-500"></i>
                口碑评价
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    name: '张先生',
                    role: '资深媒体人',
                    comment: '爆料网的内容聚合做得非常好，一站就能看到多家平台的最新爆料，省去了我每天切换多个网站的时间。',
                    rating: 5,
                  },
                  {
                    name: '李女士',
                    role: '互联网从业者',
                    comment: '作为每天关注热点的人，爆料网已经成为我获取资讯的首选平台。更新速度快，内容质量也有保障。',
                    rating: 5,
                  },
                  {
                    name: '王同学',
                    role: '大学生',
                    comment: '同学推荐我用的爆料网，确实比一个一个去找方便多了。分类清晰，想看什么类型的爆料直接选择就好。',
                    rating: 4,
                  },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{review.comment}&rdquo;
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                        <i className="ri-user-line text-secondary-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-900">{review.name}</p>
                        <p className="text-[10px] text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@baoliao.com' },
                    { icon: 'ri-phone-line', label: '联系电话', value: '010-88888888' },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区建国路88号SOHO现代城A座' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                        <p className="text-sm text-foreground-900 font-medium">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-4">
                    给我们留言
                  </h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-check-line text-2xl text-accent-600"></i>
                      </div>
                      <p className="text-foreground-900 font-medium">提交成功！</p>
                      <p className="text-sm text-foreground-500 mt-1">感谢您的留言，我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form="">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <div>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="您的姓名"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="您的邮箱"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          placeholder="请输入您的留言（500字以内）"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        ></textarea>
                      </div>
                      {formStatus === 'error' && (
                        <p className="text-xs text-primary-600">{formError}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}